import { jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useProjectStore } from '../store/project-store';
function renderMarkdown(text) {
    if (!text.trim())
        return '<p class="text-text-muted italic">No content</p>';
    let html = text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
    html = html.replace(/^### (.+)$/gm, '<h3 class="text-lg font-semibold text-text-primary mt-4 mb-2">$1</h3>');
    html = html.replace(/^## (.+)$/gm, '<h2 class="text-xl font-bold text-text-primary mt-6 mb-3">$1</h2>');
    html = html.replace(/^# (.+)$/gm, '<h1 class="text-2xl font-bold text-text-primary mt-8 mb-4">$1</h1>');
    html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    html = html.replace(/\*(.+?)\*/g, '<em>$1</em>');
    html = html.replace(/~~(.+?)~~/g, '<del>$1</del>');
    html = html.replace(/`([^`]+)`/g, '<code class="bg-bg-secondary px-1.5 py-0.5 rounded text-sm font-mono">$1</code>');
    html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="text-primary hover:underline" target="_blank" rel="noopener noreferrer">$1</a>');
    html = html.replace(/^- (.+)$/gm, '<li class="ml-4 mb-1">$1</li>');
    html = html.replace(/^(\d+)\. (.+)$/gm, '<li class="ml-4 mb-1 list-decimal">$2</li>');
    html = html.replace(/(<li class="ml-4.*<\/li>)/gs, (match) => {
        if (match.startsWith('<li class="ml-4 mb-1 list-decimal')) {
            return `<ol class="my-2">${match}</ol>`;
        }
        return `<ul class="my-2 list-disc">${match}</ul>`;
    });
    html = html.replace(/^---$/gm, '<hr class="border-card-border my-4">');
    html = html.replace(/\n{2,}/g, '</p><p class="mb-3">');
    html = `<p class="mb-3">${html}</p>`;
    html = html.replace(/<p class="mb-3"><\/p>/g, '');
    html = html.replace(/<p class="mb-3">(<h[1-3])/g, '$1');
    html = html.replace(/(<\/h[1-3]>)<\/p>/g, '$1');
    html = html.replace(/<p class="mb-3">(<ul|<ol|<hr)/g, '$1');
    html = html.replace(/(<\/ul>|<\/ol>|<hr[^>]*>)<\/p>/g, '$1');
    return html;
}
export function OverviewEditor() {
    const { projectId } = useParams();
    const { t } = useTranslation();
    const sections = useProjectStore((s) => s._sections);
    const projects = useProjectStore((s) => s.projects);
    const setSection = useProjectStore((s) => s.setSection);
    const project = projects.find((p) => p.project_id === projectId);
    const overview = projectId ? sections[projectId]?.overview || '' : '';
    const [value, setValue] = useState(overview);
    const [showSaved, setShowSaved] = useState(false);
    const textareaRef = useRef(null);
    useEffect(() => {
        setValue(overview);
    }, [overview]);
    const debouncedSave = useCallback((text) => {
        if (!projectId)
            return;
        const timer = setTimeout(() => {
            setSection(projectId, 'overview', text);
            setShowSaved(true);
            setTimeout(() => setShowSaved(false), 2000);
        }, 300);
        return () => clearTimeout(timer);
    }, [projectId, setSection]);
    useEffect(() => {
        if (value !== overview) {
            const cleanup = debouncedSave(value);
            return cleanup;
        }
    }, [value, overview, debouncedSave]);
    const insertAtCursor = (before, after) => {
        const textarea = textareaRef.current;
        if (!textarea)
            return;
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const selected = value.substring(start, end);
        const newValue = value.substring(0, start) + before + selected + after + value.substring(end);
        setValue(newValue);
        setTimeout(() => {
            textarea.focus();
            textarea.setSelectionRange(start + before.length, start + before.length + selected.length);
        }, 0);
    };
    const handleBold = () => insertAtCursor('**', '**');
    const handleItalic = () => insertAtCursor('*', '*');
    const handleH2 = () => insertAtCursor('\n## ', '\n');
    const handleH3 = () => insertAtCursor('\n### ', '\n');
    const handleList = () => insertAtCursor('\n- ', '\n');
    const handleLink = () => insertAtCursor('[', '](url)');
    const handleCode = () => insertAtCursor('`', '`');
    return (_jsxs("div", { className: "h-full flex flex-col", children: [_jsxs("div", { className: "flex items-center justify-between mb-4", children: [_jsxs("h2", { className: "text-xl font-semibold text-text-primary", children: [t('overview.title'), " \u2014 ", project?.name || projectId] }), showSaved && (_jsx("span", { className: "text-sm text-green-500", children: t('overview.saved') }))] }), _jsxs("div", { className: "flex gap-2 mb-3 border-b border-card-border pb-2", children: [_jsx("button", { onClick: handleBold, className: "btn-secondary px-2 py-1 text-sm font-bold", title: "Bold", children: "B" }), _jsx("button", { onClick: handleItalic, className: "btn-secondary px-2 py-1 text-sm italic", title: "Italic", children: "I" }), _jsx("button", { onClick: handleH2, className: "btn-secondary px-2 py-1 text-sm", title: "Heading 2", children: "H2" }), _jsx("button", { onClick: handleH3, className: "btn-secondary px-2 py-1 text-sm", title: "Heading 3", children: "H3" }), _jsx("button", { onClick: handleList, className: "btn-secondary px-2 py-1 text-sm", title: "List", children: "List" }), _jsx("button", { onClick: handleLink, className: "btn-secondary px-2 py-1 text-sm", title: "Link", children: "Link" }), _jsx("button", { onClick: handleCode, className: "btn-secondary px-2 py-1 text-sm", title: "Code", children: "Code" })] }), _jsxs("div", { className: "flex-1 flex gap-4 min-h-0", children: [_jsxs("div", { className: "flex-1 flex flex-col", children: [_jsx("label", { className: "text-sm font-medium text-text-secondary mb-2", children: t('overview.editor') }), _jsx("textarea", { ref: textareaRef, value: value, onChange: (e) => setValue(e.target.value), placeholder: t('overview.placeholder'), className: "flex-1 input resize-none font-mono text-sm p-3 min-h-[300px]" })] }), _jsxs("div", { className: "flex-1 flex flex-col", children: [_jsx("label", { className: "text-sm font-medium text-text-secondary mb-2", children: t('overview.preview') }), _jsx("div", { className: "flex-1 card p-4 overflow-auto prose prose-sm max-w-none text-text-primary min-h-[300px]", dangerouslySetInnerHTML: { __html: renderMarkdown(value) } })] })] })] }));
}
//# sourceMappingURL=OverviewEditor.js.map